import JsSIP from 'jssip';

// Environment-agnostic: always use the host that served the app (prod uses prod, dev uses dev).
const _CE_HOST = (typeof window !== 'undefined' && window.location && window.location.host)
  ? window.location.host
  : 'dev.callessential.cc';

const SIP_DOMAIN = _CE_HOST.split(':')[0];

const WSS_URL =
  `wss://${_CE_HOST}/ws-sip`;

// SIP REGISTER expiry (seconds). JsSIP re-registers at ~90% of this value.
// 3600s (1 hour) — keeps registration valid through Teams tab switches even
// when Electron/Chromium suspends the renderer process for extended periods.
const REGISTER_EXPIRES = 3600;

// How long after the last successful REGISTER 200-OK before we consider
// the socket a zombie and force a clean reconnect. Must be > REGISTER_EXPIRES
// so we don't kill a healthy connection that simply hasn't re-registered yet.
const ZOMBIE_THRESHOLD_MS = (REGISTER_EXPIRES + 300) * 1000; // 3900 s (65 min)

// How long the tab must be hidden before we assume the TCP/WSS connection
// has been torn down by the OS and force a reconnect on return.
// 5 s: Teams desktop suspends the iframe renderer very quickly, so even a
// brief switch to another Teams tab can drop the WebSocket.
const HIDDEN_RECONNECT_MS = 5_000; // 5 s

const SIP_CONFIG = {
  ws_servers: [WSS_URL],

  media_constraints: {
    audio: true,
    video: false
  },

  rtc_configuration: {
    iceServers: [
      {
        urls: [
          `stun:${SIP_DOMAIN}:3478`
        ]
      }
    ],

    bundlePolicy: 'max-compat',
    rtcpMuxPolicy: 'require',
    iceTransportPolicy: 'all'
  },

  session_timers: false,

  // Send a CRLF keep-alive every 25 s so the WSS connection survives
  // NAT idle-timeout and Asterisk's http_keepalive window.
  keep_alive_interval: 25,

  connection_recovery_min_interval: 4,
  connection_recovery_max_interval: 30
};

class SipService {

  constructor() {

    this.ua = null;

    this.session = null;

    this.sessions = [];

    this.listeners = {};

    this.userDetails = null;

    this.remoteStream = null;

    this.callStartTime = null;

    this._heartbeatTimer = null;

    this._lastRegisteredAt = 0;

    this._hiddenAt = 0;

    this.callTimer = null;

    this.isMuted = false;

    this.isOnHold = false;

    this.reconnectAttempts = 0;

    this._visibilityHandler = null;

    // Watchdog timer — detects orphaned calls when Asterisk doesn't relay BYE
    this._hangupWatchdogTimer = null;

    // Ringing timeout — terminates outgoing calls that never get answered/rejected
    this._ringTimeoutTimer = null;

    // Stats polling interval handle (RTP silence watchdog)
    this._statsInterval = null;

    this.currentCall = {
      id: null,
      direction: null,
      number: null,
      state: 'idle',
      startedAt: null,
      answeredAt: null,
      endedAt: null
    };

    this.callStats = {
      packetsLost: 0,
      jitter: 0,
      rtt: 0
    };

    this._iceServers = SIP_CONFIG.rtc_configuration.iceServers;
  }

  async _refreshIceServers() {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;
      const resp = await fetch('/api/turn-credentials', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.iceServers) this._iceServers = data.iceServers;
      }
    } catch (_) {}
  }

  _getRtcConfig() {
    return { ...SIP_CONFIG.rtc_configuration, iceServers: this._iceServers };
  }

  register(username, sipPassword, displayName = '') {

    this._refreshIceServers();

    this.unregister();

    const cleanUsername = username.includes('@')
      ? username.split('@')[0]
      : username;

    this.userDetails = {
      username: cleanUsername,
      sipPassword,
      displayName
    };

    const socket =
      new JsSIP.WebSocketInterface(
        SIP_CONFIG.ws_servers[0]
      );

    const configuration = {

      sockets: [socket],

      uri:
        `sip:${cleanUsername}@${SIP_DOMAIN}`,

      contact_uri:
        `sip:${cleanUsername}@${SIP_DOMAIN};transport=wss`,

      authorization_user: cleanUsername,

      password: sipPassword,

      display_name:
        displayName || cleanUsername,

      session_timers: false,

      register_expires: REGISTER_EXPIRES,

      hack_via_tcp: true,

      keep_alive_interval:
        SIP_CONFIG.keep_alive_interval,

      connection_recovery_min_interval:
        SIP_CONFIG.connection_recovery_min_interval,

      connection_recovery_max_interval:
        SIP_CONFIG.connection_recovery_max_interval,

      use_preloaded_route: false,

      register: true,

      user_agent:
        'CallEssentialWebDialer/2.0'
    };

    this.ua = new JsSIP.UA(configuration);

    // =====================================================
    // DEBUG LOGGING
    // =====================================================

    this.ua.on('connecting', () => {
      console.log('SIP connecting...');
    });

    this.ua.on('connected', () => {
      console.log(
        'SIP websocket connected'
      );
    });

    this.ua.on('registered', () => {
      console.log('SIP registered');
    });

    this.ua.on(
      'registrationFailed',
      (e) => {
        console.error(
          'SIP registration failed:',
          e
        );
      }
    );

    this.ua.on('disconnected', (e) => {
      console.error(
        'SIP disconnected:',
        e
      );
    });

    // =====================================================

    this.ua.on('connected', () => {

      this.reconnectAttempts = 0;

      this._lastRegisteredAt = Date.now();

      this._emit('connected', {});
    });

    this.ua.on('disconnected', (data) => {

      this._emit('disconnected', data);

      this._scheduleReconnect();
    });

    this.ua.on('registered', () => {

      this._lastRegisteredAt = Date.now();

      this._emit('registered', {});
    });

    this.ua.on(
      'registrationFailed',
      (data) => {

        this._emit(
          'registrationFailed',
          {
            cause:
              data.cause ||
              'Registration failed'
          }
        );

        this._scheduleReconnect();
      }
    );

    this.ua.on('unregistered', () => {

      this._emit('unregistered', {});
    });

    this.ua.on(
      'newRTCSession',
      (data) => {

        const session = data.session;

        // Asterisk retransmits the INVITE after a CANCEL/487 race, making the
        // dialer ring a second time. If we already have an active session,
        // reject the duplicate immediately so the caller only needs to hang up once.
        if (this.session && data.originator === 'remote') {
          try { session.terminate({ status_code: 486, reason_phrase: 'Busy Here' }); } catch (_) {}
          return;
        }

        this.session = session;

        this.sessions.push(session);

        this.currentCall.id =
          session.id || Date.now();

        this.currentCall.direction =
          data.originator === 'remote'
            ? 'incoming'
            : 'outgoing';

        this.currentCall.number =
          session.remote_identity
            ?.uri?.user || '';

        this.currentCall.startedAt =
          new Date();

        this._setupSessionHandlers(
          session
        );

        if (
          data.originator === 'remote'
        ) {

          // Display name format from Asterisk: "Q:Queue Name" = queue call
          const _rawName = session.remote_identity?.display_name || '';
          const _isQueue = _rawName.startsWith('Q:');
          const _queueName = _isQueue ? _rawName.slice(2).trim() : null;

          // Try custom SIP header first, fall back to display_name convention
          let _headerQueue = null;
          try { _headerQueue = session.request?.getHeader('x-queue-name') || null; } catch(_) {}

          this._emit(
            'incomingCall',
            {
              from:
                session.remote_identity
                  ?.uri?.user ||
                'Unknown',

              fromName: _isQueue ? '' : _rawName,

              queueName: _headerQueue || _queueName || null,
            }
          );
        }
      }
    );

    this.ua.start();
    // Prevent background CPU throttling and OS-level sleep
    this._acquireKeepAlives();

    // =====================================================
    // VISIBILITY RECOVERY
    // =====================================================

    if (this._visibilityHandler) {

      document.removeEventListener(
        'visibilitychange',
        this._visibilityHandler
      );
    }

    this._visibilityHandler = () => {

      if (document.hidden) {
        this._hiddenAt = Date.now();
        return;
      }

      if (!this.ua) return;

      const wasHiddenMs = this._hiddenAt
        ? (Date.now() - this._hiddenAt)
        : 0;

      this._hiddenAt = 0;

      if (wasHiddenMs > HIDDEN_RECONNECT_MS) {
        // After 30+ seconds hidden, assume the TCP/WSS connection may have
        // been torn down by the OS or a NAT — force a clean reconnect.
        this._forceReconnect();
        return;
      }

      if (
        !this.ua.isConnected() ||
        !this.ua.isRegistered()
      ) {
        try { this.ua.start(); } catch (_) {}
      }
    };

    document.addEventListener(
      'visibilitychange',
      this._visibilityHandler
    );

    this._lastRegisteredAt = Date.now();

    if (this._heartbeatTimer) clearInterval(this._heartbeatTimer);
    this._heartbeatTimer = setInterval(() => {
      if (!this.ua) return;
      if (!this.ua.isConnected()) {
        try { this.ua.start(); } catch (_) {}
        return;
      }
      // JsSIP re-registers at ~90% of REGISTER_EXPIRES (every ~270 s).
      // Only consider the socket a zombie if no successful re-REGISTER
      // has arrived within ZOMBIE_THRESHOLD_MS (REGISTER_EXPIRES + 60 s).
      if (Date.now() - this._lastRegisteredAt > ZOMBIE_THRESHOLD_MS) {
        this._forceReconnect();
      }
    }, 15000);
  }

  _setupSessionHandlers(session) {

    session.on('progress', () => {

      this.currentCall.state =
        'ringing';

      this._emit('callState', {
        state: 'ringing'
      });

      // For outgoing calls: if Asterisk never delivers a reject/cancel/BYE,
      // force-terminate after 60 s of unanswered ringing so the UI doesn't
      // get stuck. (Incoming calls are handled by the caller's own timeout.)
      if (this.currentCall.direction === 'outgoing') {
        clearTimeout(this._ringTimeoutTimer);
        this._ringTimeoutTimer = setTimeout(() => {
          if (this.currentCall.state === 'ringing' && this.session) {
            console.warn('SipService: ringing timeout (60s) — no answer, terminating');
            try { this.session.terminate(); } catch (_) {
              this._handleCallTermination('failed', 'No answer');
            }
          }
        }, 60000);
      }
    });

    session.on('confirmed', () => {

      this.callStartTime =
        Date.now();

      this.currentCall.state =
        'connected';

      this.currentCall.answeredAt =
        new Date();

      this._startCallTimer();

      this._attachRemoteAudio(
        session
      );

      this._startStatsCollector(
        session
      );

      this._emit('callState', {
        state: 'connected'
      });
    });

    session.on('ended', (data) => {

      this._handleCallTermination(
        'ended',
        data?.cause ||
          'Normal clearing'
      );
    });

    session.on('failed', (data) => {

      this._handleCallTermination(
        'failed',
        data?.cause || 'Call failed'
      );
    });

    session.on('hold', () => {

      this.isOnHold = true;

      this._emit('callHold', {
        enabled: true
      });
    });

    session.on('unhold', () => {

      this.isOnHold = false;

      this._emit('callHold', {
        enabled: false
      });
    });

    session.on(
      'peerconnection',
      (data) => {

        const pc =
          data.peerconnection;

        console.log(
          'PeerConnection created'
        );

        pc.addEventListener(
          'iceconnectionstatechange',
          () => {
            const iceState = pc.iceConnectionState;
            console.log('ICE state:', iceState);

            // ICE failed with no BYE — force terminate after 2.5s
            if (iceState === 'failed' && this.currentCall.state === 'connected') {
              clearTimeout(this._hangupWatchdogTimer);
              this._hangupWatchdogTimer = setTimeout(() => {
                if (this.currentCall.state === 'connected' && this.session) {
                  console.warn('SipService: ICE failed — terminating orphaned call');
                  try { this.session.terminate(); } catch (_) {
                    // terminate() failed — session already dead, clean up directly
                    this._handleCallTermination('ended', 'ICE failed');
                  }
                }
              }, 2500);
            }
          }
        );

        pc.addEventListener(
          'connectionstatechange',
          () => {
            const connState = pc.connectionState;
            console.log('Peer state:', connState);

            if (connState === 'connected') {
              // Clear any pending watchdog — connection recovered
              clearTimeout(this._hangupWatchdogTimer);
              this._hangupWatchdogTimer = null;
              return;
            }

            if (this.currentCall.state !== 'connected') return;

            if (connState === 'closed') {
              // Asterisk cleanly tore down the WebRTC connection (remote hangup
              // that didn't come through as a SIP BYE over WebSocket). Give
              // JsSIP 1.5s to fire the 'ended' event first, then force-terminate.
              clearTimeout(this._hangupWatchdogTimer);
              this._hangupWatchdogTimer = setTimeout(() => {
                if (this.currentCall.state === 'connected' && this.session) {
                  console.warn('SipService: WebRTC connection closed — terminating orphaned call');
                  try { this.session.terminate(); } catch (_) {
                    this._handleCallTermination('ended', 'Remote hangup');
                  }
                }
              }, 1500);
            } else if (connState === 'failed') {
              // Hard failure — give 2.5s for a BYE then force-terminate
              clearTimeout(this._hangupWatchdogTimer);
              this._hangupWatchdogTimer = setTimeout(() => {
                if (this.currentCall.state === 'connected' && this.session) {
                  console.warn('SipService: WebRTC peer failed — terminating orphaned call');
                  try { this.session.terminate(); } catch (_) {
                    this._handleCallTermination('ended', 'Connection failed');
                  }
                }
              }, 2500);
            } else if (connState === 'disconnected') {
              // Transient disconnect — give 8s for ICE restart before terminating
              clearTimeout(this._hangupWatchdogTimer);
              this._hangupWatchdogTimer = setTimeout(() => {
                if (this.currentCall.state === 'connected' && this.session) {
                  console.warn('SipService: WebRTC peer disconnected — terminating orphaned call');
                  try { this.session.terminate(); } catch (_) {
                    this._handleCallTermination('ended', 'Connection disconnected');
                  }
                }
              }, 8000);
            }
          }
        );

        pc.addEventListener(
          'track',
          (event) => {

            const remoteAudio =
              document.getElementById(
                'remoteAudio'
              );

            if (!remoteAudio) {

              console.error(
                'remoteAudio element not found'
              );

              return;
            }

            if (!this.remoteStream) {

              this.remoteStream =
                new MediaStream();
            }

            if (
              event.track &&
              !this.remoteStream
                .getTracks()
                .includes(
                  event.track
                )
            ) {

              this.remoteStream.addTrack(
                event.track
              );
            }

            remoteAudio.srcObject =
              this.remoteStream;

            remoteAudio.muted = false;

            remoteAudio.volume = 1.0;

            remoteAudio.play().catch(
              (e) => {

                console.warn(
                  'Remote audio playback blocked:',
                  e
                );
              }
            );

            // When Asterisk hangs up the call it stops the RTP stream, which
            // causes the remote audio track to fire 'ended'. This is a reliable
            // fallback for the case where Asterisk doesn't relay the BYE over
            // the WebSocket transport (common with pjsip WebSocket endpoints).
            if (event.track) {
              event.track.addEventListener('ended', () => {
                console.warn('SipService: remote audio track ended');
                if (this.currentCall.state === 'connected' && this.session) {
                  clearTimeout(this._hangupWatchdogTimer);
                  this._hangupWatchdogTimer = setTimeout(() => {
                    if (this.currentCall.state === 'connected' && this.session) {
                      console.warn('SipService: remote track ended — terminating orphaned call');
                      try { this.session.terminate(); } catch (_) {
                        this._handleCallTermination('ended', 'Remote hangup');
                      }
                    }
                  }, 2000);
                }
              });
            }
          }
        );
      }
    );
  }

  // =====================================================
  // REMAINING METHODS UNCHANGED
  // =====================================================

  _handleCallTermination(state, cause) {

    clearTimeout(this._hangupWatchdogTimer);
    this._hangupWatchdogTimer = null;

    clearTimeout(this._ringTimeoutTimer);
    this._ringTimeoutTimer = null;

    clearInterval(this._statsInterval);
    this._statsInterval = null;

    // Terminate any lingering sessions (Asterisk retransmit duplicates, etc.)
    this.sessions.forEach(s => {
      if (s && s !== this.session) {
        try { s.terminate(); } catch (_) {}
      }
    });
    this.sessions = [];

    this._stopCallTimer();

    this._clearRemoteAudio();

    this.currentCall.state =
      state;

    this.currentCall.endedAt =
      new Date();

    this.isMuted = false;

    this.isOnHold = false;

    this._emit('callState', {
      state,
      cause
    });

    this._emit('callSummary', {
      direction:
        this.currentCall.direction,

      number:
        this.currentCall.number,

      state,

      cause,

      duration:
        this._getCurrentDuration(),

      startedAt:
        this.currentCall.startedAt,

      answeredAt:
        this.currentCall.answeredAt,

      endedAt:
        this.currentCall.endedAt
    });

    this.session = null;
  }

  _attachRemoteAudio(session) {

    try {

      const remoteAudio =
        document.getElementById(
          'remoteAudio'
        );

      if (
        !remoteAudio ||
        !session.connection
      ) {
        return;
      }

      const receivers =
        session.connection
          .getReceivers
          ? session.connection.getReceivers()
          : [];

      if (!this.remoteStream) {

        this.remoteStream =
          new MediaStream();
      }

      receivers.forEach(
        (receiver) => {

          if (
            receiver.track &&
            receiver.track.kind ===
              'audio' &&
            !this.remoteStream
              .getTracks()
              .includes(
                receiver.track
              )
          ) {

            this.remoteStream.addTrack(
              receiver.track
            );
          }
        }
      );

      remoteAudio.srcObject =
        this.remoteStream;

      remoteAudio.muted = false;

      remoteAudio.volume = 1.0;

      remoteAudio.play().catch(
        (e) => {

          console.warn(
            'Remote audio playback failed:',
            e
          );
        }
      );
    } catch (e) {

      console.error(
        'Failed to attach remote audio:',
        e
      );
    }
  }

  _clearRemoteAudio() {

    const remoteAudio =
      document.getElementById(
        'remoteAudio'
      );

    if (remoteAudio) {

      remoteAudio.pause();

      remoteAudio.srcObject =
        null;
    }

    if (this.remoteStream) {

      this.remoteStream
        .getTracks()
        .forEach((track) => {

          try {
            track.stop();
          } catch (e) {}
        });
    }

    this.remoteStream = null;
  }

  makeCall(number, fromNumber = '', preAcquiredStream = null) {
    if (!this.ua || !this.ua.isRegistered()) {
      this._emit('error', { message: 'Not registered' });
      return null;
    }

    // Catch zombie state: isRegistered() can lag after WebSocket dies.
    if (!this.ua.isConnected()) {
      this._emit('error', { message: 'Connection lost — reconnecting, please try again in a few seconds.' });
      this._forceReconnect();
      return null;
    }

    const cleanNumber = number.trim();
    const cleanFrom = fromNumber.trim();
    const target = `sip:${cleanNumber}@${SIP_DOMAIN}`;

    // Use pre-acquired stream when available (iOS: avoids a second getUserMedia
    // call after an await that would break the activation context).
    const options = preAcquiredStream
      ? {
          mediaStream: preAcquiredStream,
          pcConfig: this._getRtcConfig(),
          rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
          extraHeaders: []
        }
      : {
          mediaConstraints: SIP_CONFIG.media_constraints,
          pcConfig: this._getRtcConfig(),
          rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
          extraHeaders: []
        };

    if (cleanFrom) {
      options.extraHeaders.push(`P-Preferred-Identity: <sip:${cleanFrom}@${SIP_DOMAIN}>`);
      options.extraHeaders.push(`X-Selected-CLI: ${cleanFrom}`);
    }

    this.currentCall.direction = 'outgoing';
    this.currentCall.number = cleanNumber;
    this.currentCall.state = 'calling';

    this.session = this.ua.call(target, options);

    if (this.session) {
      // Trickle ICE: JsSIP by default waits for ICE gathering to be fully
      // "complete" before sending the INVITE.  If TURN allocation requests
      // time out (30 s+), gathering never fires "complete" and the call
      // hangs until our 15-second app timeout kills it.
      //
      // JsSIP exposes a per-candidate `ready` callback on the 'icecandidate'
      // session event.  Calling ready() immediately sends the INVITE with
      // whatever candidates are in localDescription at that moment.
      //
      // Strategy: wait for the first server-reflexive (STUN-derived) candidate
      // so we have a routable external address, then trigger the INVITE.
      // If only host candidates ever arrive (STUN also unreachable), fall back
      // to the first host candidate after a 500 ms wait.
      let _iceReadyFired = false;
      let _iceHostTimer = null;

      this.session.on('icecandidate', ({ candidate, ready }) => {
        if (_iceReadyFired) return;

        if (candidate && candidate.type === 'srflx') {
          // Best case: external (STUN-derived) address available.
          _iceReadyFired = true;
          if (_iceHostTimer) { clearTimeout(_iceHostTimer); _iceHostTimer = null; }
          ready();
        } else if (candidate && candidate.type === 'host' && !_iceHostTimer) {
          // Fallback: we have at least a local address — start 500 ms timer
          // in case srflx never arrives (STUN unreachable).
          _iceHostTimer = setTimeout(() => {
            if (!_iceReadyFired) { _iceReadyFired = true; ready(); }
          }, 500);
        } else if (candidate && candidate.type === 'relay') {
          // TURN relay arrived (rare with current setup, but accept it).
          _iceReadyFired = true;
          if (_iceHostTimer) { clearTimeout(_iceHostTimer); _iceHostTimer = null; }
          ready();
        }
      });
    }

    // If no early ringing in 15s the call is stuck (ICE hang or dead socket).
    const callSetupTimeout = setTimeout(() => {
      if (this.session && this.currentCall.state === 'calling') {
        console.warn('Call setup timeout — no progress in 15s, terminating');
        try { this.session.terminate(); } catch (_) {}
        this._emit('error', { message: 'Call timed out. Please check your connection and try again.' });
      }
    }, 15000);

    const clearSetupTimeout = () => clearTimeout(callSetupTimeout);
    this.session.on('progress', clearSetupTimeout);
    this.session.on('confirmed', clearSetupTimeout);
    this.session.on('failed', clearSetupTimeout);
    this.session.on('ended', clearSetupTimeout);

    this._emit('callState', { state: 'calling' });

    return this.session;
  }

  answerCall(preAcquiredStream = null) {
    if (!this.session) return;
    // Same early-ready ICE shortcut as makeCall (fix 2026-07-08): send the
    // 200 OK as soon as a usable candidate arrives instead of waiting for
    // full ICE gathering — VPN/overlay interfaces on the user's machine can
    // stall gathering for 40+ s, blowing past Asterisk's 30 s dial timeout
    // so the answer never completed.
    let _iceReadyFired = false;
    let _iceHostTimer = null;
    this.session.on('icecandidate', ({ candidate, ready }) => {
      if (_iceReadyFired) return;
      if (candidate && (candidate.type === 'srflx' || candidate.type === 'relay')) {
        _iceReadyFired = true;
        if (_iceHostTimer) { clearTimeout(_iceHostTimer); _iceHostTimer = null; }
        ready();
      } else if (candidate && candidate.type === 'host' && !_iceHostTimer) {
        _iceHostTimer = setTimeout(() => {
          if (!_iceReadyFired) { _iceReadyFired = true; ready(); }
        }, 500);
      }
    });
    const opts = preAcquiredStream
      ? { mediaStream: preAcquiredStream, pcConfig: this._getRtcConfig() }
      : { mediaConstraints: SIP_CONFIG.media_constraints, pcConfig: this._getRtcConfig() };
    this.session.answer(opts);
  }

  rejectCall() {
    if (this.session) {
      this.session.terminate();
      this.session = null;
    }
  }

  hangup() {
    if (this.session) {
      this.session.terminate();
      this.session = null;
    }
  }

  hold() {
    if (!this.session) return;
    this.session.hold();
  }

  resume() {
    if (!this.session) return;
    this.session.unhold();
  }

  mute(enabled = true) {
    if (!this.session || !this.session.connection) return;

    this.isMuted = enabled;

    this.session.connection.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = !enabled;
      }
    });

    this._emit('callMute', { enabled });
  }

  sendDTMF(tone) {
    if (this.session) {
      this.session.sendDTMF(tone);
    }
  }

  transfer(targetNumber) {
    if (!this.session) {
      this._emit('transferFailed', { reason: 'No active call to transfer.' });
      return false;
    }
    try {
      const result = this.session.refer(`sip:${targetNumber}@${SIP_DOMAIN}`);
      if (result === false) {
        this._emit('transferFailed', { reason: 'Call is not in a transferable state.' });
        return false;
      }
      this._emit('callTransfer', { type: 'blind', target: targetNumber });
      return true;
    } catch (e) {
      this._emit('transferFailed', { reason: e.message || 'Transfer failed.' });
      return false;
    }
  }

  initiateAttendedTransfer(targetNumber) {
    this._emit('attendedTransfer', { target: targetNumber, status: 'initiated' });
  }

  _startCallTimer() {
    this._stopCallTimer();

    this.callTimer = setInterval(() => {
      if (!this.callStartTime) return;

      const duration = this._getCurrentDuration();

      this._emit('callTimer', {
        duration,
        formatted: this._formatDuration(duration)
      });
    }, 1000);
  }

  _stopCallTimer() {
    if (this.callTimer) {
      clearInterval(this.callTimer);
      this.callTimer = null;
    }

    this.callStartTime = null;
  }

  _getCurrentDuration() {
    if (!this.callStartTime) return 0;
    return Math.floor((Date.now() - this.callStartTime) / 1000);
  }

  _formatDuration(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (num) => String(num).padStart(2, '0');

    if (hours > 0) {
      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }

    return `${pad(minutes)}:${pad(seconds)}`;
  }

  async _startStatsCollector(session) {
    if (!session || !session.connection) return;

    const pc = session.connection;
    let lastPacketCount = 0;
    let silenceStreak = 0;
    let hasEstablishedAudio = false;
    let statsInProgress = false;

    if (this._statsInterval) clearInterval(this._statsInterval);

    this._statsInterval = setInterval(async () => {
      if (!this.session) {
        clearInterval(this._statsInterval);
        this._statsInterval = null;
        return;
      }
      if (statsInProgress) return;
      statsInProgress = true;

      try {
        const stats = await pc.getStats();
        let currentPackets = 0;

        stats.forEach((report) => {
          if (report.type === 'candidate-pair') {
            this.callStats.rtt = report.currentRoundTripTime || 0;
          }
          if (report.type === 'inbound-rtp' && report.kind === 'audio') {
            this.callStats.jitter = report.jitter || 0;
            this.callStats.packetsLost = report.packetsLost || 0;
            currentPackets += report.packetsReceived || 0;
          }
        });

        // Wait until ≥25 packets received (~0.5 s of audio) before watching
        // for silence, so call-setup transients don't trigger a false hangup.
        if (currentPackets > 25) hasEstablishedAudio = true;

        if (hasEstablishedAudio && this.currentCall.state === 'connected' && !this.isOnHold) {
          if (currentPackets > lastPacketCount) {
            silenceStreak = 0;
          } else {
            silenceStreak++;
            // 3 × 500 ms = 1.5 s of RTP silence → remote party has hung up
            if (silenceStreak >= 3) {
              console.warn('SipService: RTP silence for ~3s — remote party hung up');
              clearInterval(this._statsInterval);
              this._statsInterval = null;
              if (this.currentCall.state === 'connected' && this.session) {
                try { this.session.terminate(); } catch (_) {
                  this._handleCallTermination('ended', 'Remote hangup');
                }
              }
              return;
            }
          }
        }

        lastPacketCount = currentPackets;

        this._emit('callStats', this.callStats);
      } catch (e) {}
      finally {
        statsInProgress = false;
      }
    }, 500);
  }

  _scheduleReconnect() {
    if (!this.userDetails || this.reconnectAttempts > 5) return;

    this.reconnectAttempts++;

    setTimeout(() => {
      if (!this.ua || this.ua.isConnected()) return;

      this.register(
        this.userDetails.username,
        this.userDetails.sipPassword,
        this.userDetails.displayName
      );
    }, 3000 * this.reconnectAttempts);
  }

  // ── Background keep-alive helpers ─────────────────────────────────────────
  // Chromium throttles timers and may suspend tabs that are backgrounded or
  // in minimised windows.  We fight this with two mechanisms:
  //
  //  a) Screen Wake Lock  — signals to the browser that "something important
  //     is happening here"; keeps the renderer awake even when minimised.
  //     Re-requested on visibilitychange because the browser releases it
  //     automatically when the page becomes hidden.
  //
  //  b) Silent AudioContext — a near-zero-volume oscillator keeps the audio
  //     pipeline active.  Chromium does not aggressively throttle tabs that
  //     are actively using audio, even in the background.
  //
  // These are belt-and-suspenders alongside the Teams lifecycle fixes
  // (readyToResume, visibilitychange handler, push notifications).
  _acquireKeepAlives() {
    this._releaseKeepAlives(); // Clean up any previous instance

    // (a) Screen Wake Lock
    if ('wakeLock' in navigator) {
      const acquire = async () => {
        try {
          this._screenWakeLock = await navigator.wakeLock.request('screen');
          this._screenWakeLock.addEventListener('release', () => {
            this._screenWakeLock = null;
          });
        } catch (_) {}
      };
      acquire();
      // Re-acquire when page comes back to foreground
      const onVis = () => {
        if (document.visibilityState === 'visible' && !this._screenWakeLock) {
          acquire();
        }
      };
      document.addEventListener('visibilitychange', onVis);
      this._visOnWakeLock = onVis; // stored so we can remove it later
    }

    // (b) Silent AudioContext — volume is 0.00001 (completely inaudible)
    try {
      if (!this._silentCtx || this._silentCtx.state === 'closed') {
        const ctx  = new AudioContext();
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.value = 0.00001;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        this._silentCtx  = ctx;
        this._silentNode = osc;
      }
    } catch (_) {}
  }

  _releaseKeepAlives() {
    try { this._screenWakeLock?.release(); } catch (_) {}
    this._screenWakeLock = null;
    if (this._visOnWakeLock) {
      document.removeEventListener('visibilitychange', this._visOnWakeLock);
      this._visOnWakeLock = null;
    }
    try { this._silentNode?.stop(); } catch (_) {}
    try { this._silentCtx?.close(); } catch (_) {}
    this._silentCtx  = null;
    this._silentNode = null;
  }

  // Public method for Teams SDK lifecycle handler to call on tab resume
  wakeUp() {
    this._forceReconnect();
  }

  _forceReconnect() {
    if (!this.ua) return;
    // Never restart the UA while a call is ringing or active: ua.stop()
    // terminates the session (an unanswered incoming INVITE gets replied 480),
    // which made answering impossible when the window regained focus after
    // being hidden > HIDDEN_RECONNECT_MS (5 s). Fix 2026-07-08.
    if (this.session) return;
    // Reset both the liveness timer and the reconnect counter so that
    // the heartbeat does not re-trigger immediately and _scheduleReconnect
    // can try again if this reconnect also fails.
    this._lastRegisteredAt = Date.now();
    this.reconnectAttempts = 0;
    try { this.ua.stop(); } catch (_) {}
    setTimeout(() => {
      if (!this.ua) return;
      try { this.ua.start(); } catch (_) {}
    }, 500);
  }

  unregister() {
    this._stopCallTimer();

    clearTimeout(this._hangupWatchdogTimer);
    this._hangupWatchdogTimer = null;

    clearTimeout(this._ringTimeoutTimer);
    this._ringTimeoutTimer = null;

    clearInterval(this._statsInterval);
    this._statsInterval = null;

    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }

    // Release background keep-alive locks when unregistering
    this._releaseKeepAlives();

    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }

    if (this.ua) {
      try {
        this.ua.stop();
      } catch (e) {}
    }

    this._clearRemoteAudio();

    this.ua = null;
    this.session = null;
    this.sessions = [];
  }

  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }

    this.listeners[event].push(callback);
  }

  off(event, callback) {
    if (!this.listeners[event]) return;

    this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback);
  }

  _emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach((cb) => cb(data));
    }
  }
}

const sipService = new SipService();

export default sipService;